import React, { useCallback, useEffect, useRef, useState } from "react"

import {
  confirmLinkedInProposal,
  getLinkedInProposals,
  rejectLinkedInProposal,
} from "@actions/contactActions"
import { Caption, FontWeight, Subtitle, Title6 } from "@assets/styles/typographyStyles"
import { ButtonEl } from "@components/common"
import { BUTTON_VARIANTS } from "@constants/button"

import {
  ProposalActions,
  ProposalLeft,
  ProposalRow,
  ProposalsWrapper,
} from "./linkedInStyles"

// T-W2-F2 (2026-07-04): pending LinkedIn name-match proposals -- rows from the user's
// Connections.csv that had NO provable email match against an existing contact
// (api/routers/counterpart_search.py never auto-merges a name-only match, the Matt-Lock
// law). Each row waits here for an explicit Confirm/reject. Honest-empty: renders nothing
// while loading or once the queue is empty -- never a fabricated "0 pending" row (same
// convention as DeviceSyncedRow above it on this page).
export const LinkedInProposals = ({ refreshKey }) => {
  const [proposals, setProposals] = useState(null)
  const [resolvingId, setResolvingId] = useState(null)
  const [affirmation, setAffirmation] = useState(null)
  const affirmationTimerRef = useRef(null)

  const load = useCallback(() => {
    getLinkedInProposals("pending").then(setProposals)
  }, [])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  useEffect(() => {
    return () => {
      if (affirmationTimerRef.current) clearTimeout(affirmationTimerRef.current)
    }
  }, [])

  const showAffirmation = message => {
    setAffirmation(message)
    if (affirmationTimerRef.current) clearTimeout(affirmationTimerRef.current)
    affirmationTimerRef.current = setTimeout(() => setAffirmation(null), 4000)
  }

  const proposalName = proposal =>
    [proposal.first_name, proposal.last_name].filter(Boolean).join(" ") ||
    "Unnamed connection"

  const handleConfirm = async proposal => {
    if (!proposal.candidate_contact_id) return
    setResolvingId(proposal.id)
    try {
      await confirmLinkedInProposal(proposal.id, proposal.candidate_contact_id)
      setProposals(prev => prev.filter(p => p.id !== proposal.id))
      showAffirmation(`${proposal.candidate_name || proposalName(proposal)} updated`)
    } catch (error) {
      console.log("LinkedIn proposal confirm error", error)
    } finally {
      setResolvingId(null)
    }
  }

  const handleReject = async proposal => {
    setResolvingId(proposal.id)
    try {
      await rejectLinkedInProposal(proposal.id)
      setProposals(prev => prev.filter(p => p.id !== proposal.id))
    } catch (error) {
      console.log("LinkedIn proposal reject error", error)
    } finally {
      setResolvingId(null)
    }
  }

  if (!proposals || proposals.length === 0) return null

  return (
    <ProposalsWrapper data-testid="linkedinProposalsQueue">
      <Title6>LinkedIn matches to confirm</Title6>
      <Caption style={{ color: "#767676", marginTop: 4, marginBottom: 8 }}>
        These LinkedIn connections don&apos;t have an email on file. Confirm they&apos;re
        the right contact before their company/title is attached.
      </Caption>

      {affirmation && (
        <Caption
          data-testid="linkedinProposalAffirmation"
          style={{ color: "var(--success, #1a7f37)", marginBottom: 8 }}>
          {affirmation}
        </Caption>
      )}

      {proposals.map(proposal => (
        <ProposalRow key={proposal.id} data-testid="linkedinProposalRow">
          <ProposalLeft>
            <Subtitle style={{ fontWeight: FontWeight.medium }}>
              {proposalName(proposal)}
            </Subtitle>
            <Caption style={{ color: "var(--gray)" }}>
              {[proposal.position, proposal.company].filter(Boolean).join(" at ") ||
                "No title/company on file"}
            </Caption>
            <Caption style={{ color: "var(--gray)" }}>
              {proposal.candidate_name
                ? `Might be: ${proposal.candidate_name}`
                : "No matching contact found by name. Reject if not relevant."}
            </Caption>
          </ProposalLeft>

          <ProposalActions>
            <ButtonEl
              autoWidth
              padding="8px 12px"
              backgroundColor="var(--lightGray4)"
              border="1px solid var(--gray3)"
              textColor="var(--black3)"
              loadingColor="var(--lightGray3)"
              variant={BUTTON_VARIANTS.ROUNDED}
              text="Confirm"
              testID="linkedinProposalConfirm"
              isLoading={resolvingId === proposal.id}
              disabled={!proposal.candidate_contact_id || resolvingId === proposal.id}
              onClick={() => handleConfirm(proposal)}
            />
            <ButtonEl
              autoWidth
              padding="8px 12px"
              backgroundColor="transparent"
              border="1px solid var(--gray3)"
              textColor="var(--darkGray)"
              loadingColor="var(--lightGray3)"
              variant={BUTTON_VARIANTS.ROUNDED}
              text="Not the same person"
              testID="linkedinProposalReject"
              isLoading={resolvingId === proposal.id}
              disabled={resolvingId === proposal.id}
              onClick={() => handleReject(proposal)}
            />
          </ProposalActions>
        </ProposalRow>
      ))}
    </ProposalsWrapper>
  )
}
